import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Box, Checkbox, FullColumn, FullRow, GridItem, IconButton, Row, TextButton, Typography } from 'seagull';
import { CloseCross } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { ClientContactPicker } from '../ClientContactPicker/ClientContactPicker';
import { HintIcon } from '../HintIcon/HintIcon';
import { contactToReviewer, eligibleForRow, reviewerToContactKey } from './ClientAdditionalRecipients.utils';
export const GetClientAdditionalRecipientsTID = (parentTestId)=>getTestIds(parentTestId);
export const ClientAdditionalRecipients = (param)=>{
    let { clientContacts, primaryContact, reviewers, onChange, onCreateContact, showReviewerErrors, disabled = false, testId } = param;
    const { t: contactPickerTexts } = useLocaleKeys('agreementStudio.proposalMode.contactPicker');
    const { t: agreementStudioTexts } = useLocaleKeys('components.agreementStudio');
    const { ClientAdditionalRecipientsTestIds } = useMemo(()=>GetClientAdditionalRecipientsTID(testId), [
        testId
    ]);
    const contactsByEmail = useMemo(()=>{
        const map = new Map();
        clientContacts.forEach((contact)=>map.set(contact.email().toLowerCase(), contact));
        return map;
    }, [
        clientContacts
    ]);
    const rowSelections = useMemo(()=>reviewers.map((reviewer)=>{
            var _contactsByEmail_get;
            return (_contactsByEmail_get = contactsByEmail.get(reviewerToContactKey(reviewer))) !== null && _contactsByEmail_get !== void 0 ? _contactsByEmail_get : null;
        }), [
        reviewers,
        contactsByEmail
    ]);
    const replaceRowFromContact = (rowIndex, contact)=>{
        const next = reviewers.slice();
        if (contact == null) {
            var _ref;
            var _next_rowIndex;
            next[rowIndex] = {
                firstName: '',
                lastName: '',
                email: '',
                isSignatory: (_ref = (_next_rowIndex = next[rowIndex]) === null || _next_rowIndex === void 0 ? void 0 : _next_rowIndex.isSignatory) !== null && _ref !== void 0 ? _ref : false
            };
        } else {
            var _ref1;
            var _next_rowIndex1;
            next[rowIndex] = contactToReviewer(contact, (_ref1 = (_next_rowIndex1 = next[rowIndex]) === null || _next_rowIndex1 === void 0 ? void 0 : _next_rowIndex1.isSignatory) !== null && _ref1 !== void 0 ? _ref1 : false);
        }
        onChange(next);
    };
    const toggleRequiredToSign = (rowIndex, isSignatory)=>{
        const next = reviewers.slice();
        next[rowIndex] = _object_spread_props(_object_spread({}, next[rowIndex]), {
            isSignatory
        });
        onChange(next);
    };
    const removeRow = (rowIndex)=>{
        onChange(reviewers.filter((_, index)=>index !== rowIndex));
    };
    const addEmptyRow = ()=>{
        onChange([
            ...reviewers,
            {
                firstName: '',
                lastName: '',
                email: '',
                isSignatory: false
            }
        ]);
    };
    return /*#__PURE__*/ _jsx(GridItem, {
        span: 12,
        testId: ClientAdditionalRecipientsTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(FullColumn, {
            gap: 12,
            children: [
                reviewers.map((reviewer, index)=>{
                    var _reviewer_isSignatory;
                    const selected = rowSelections[index];
                    const eligible = eligibleForRow({
                        clientContacts,
                        primaryContact,
                        reviewers,
                        rowIndex: index
                    });
                    const isIncomplete = showReviewerErrors && selected == null;
                    const rowKey = reviewer.clientContactId || reviewer.email || "row-".concat(index);
                    return /*#__PURE__*/ _jsxs(FullColumn, {
                        gap: 4,
                        testId: ClientAdditionalRecipientsTestIds.Row(index),
                        children: [
                            /*#__PURE__*/ _jsxs(FullRow, {
                                gap: 8,
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        style: {
                                            flex: 1,
                                            minWidth: 0
                                        },
                                        children: /*#__PURE__*/ _jsx(ClientContactPicker, {
                                            testId: ClientAdditionalRecipientsTestIds.RowPicker(index),
                                            contacts: eligible,
                                            value: selected,
                                            disabled: disabled,
                                            onCreateContact: onCreateContact,
                                            label: contactPickerTexts.additionalRecipientLabel(),
                                            placeholder: contactPickerTexts.signatoryPlaceholder(),
                                            onChange: (contact)=>replaceRowFromContact(index, contact)
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(Row, {
                                        gap: 12,
                                        alignItems: "center",
                                        children: [
                                            /*#__PURE__*/ _jsxs(Row, {
                                                gap: 4,
                                                alignItems: "center",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Checkbox, {
                                                        testId: ClientAdditionalRecipientsTestIds.RowRequiredToSign(index),
                                                        bi: {
                                                            name: 'required_to_sign'
                                                        },
                                                        checked: (_reviewer_isSignatory = reviewer.isSignatory) !== null && _reviewer_isSignatory !== void 0 ? _reviewer_isSignatory : false,
                                                        onChange: (e)=>toggleRequiredToSign(index, e.target.checked),
                                                        disabled: disabled,
                                                        children: /*#__PURE__*/ _jsx(Typography, {
                                                            noWrap: true,
                                                            children: agreementStudioTexts.requiredToSign()
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(HintIcon, {
                                                        info: contactPickerTexts.requiredToSignTooltip()
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(IconButton, {
                                                testId: ClientAdditionalRecipientsTestIds.RowRemove(index),
                                                bi: {
                                                    name: 'remove_recipient'
                                                },
                                                disabled: disabled,
                                                tooltip: {
                                                    title: contactPickerTexts.removeRecipient()
                                                },
                                                onClick: ()=>removeRow(index),
                                                children: /*#__PURE__*/ _jsx(CloseCross, {})
                                            })
                                        ]
                                    })
                                ]
                            }),
                            isIncomplete && /*#__PURE__*/ _jsx(Typography, {
                                skin: "error",
                                variant: "paragraph2",
                                testId: ClientAdditionalRecipientsTestIds.RowError(index),
                                children: contactPickerTexts.recipientRequired()
                            })
                        ]
                    }, rowKey);
                }),
                /*#__PURE__*/ _jsxs(Row, {
                    gap: 4,
                    alignItems: "center",
                    children: [
                        /*#__PURE__*/ _jsx(TextButton, {
                            underline: "none",
                            testId: ClientAdditionalRecipientsTestIds.AddButton(),
                            bi: {
                                name: 'add_additional_recipient'
                            },
                            disabled: disabled,
                            onClick: addEmptyRow,
                            children: contactPickerTexts.addAdditionalRecipientsButtonLabel()
                        }),
                        /*#__PURE__*/ _jsx(HintIcon, {
                            testId: ClientAdditionalRecipientsTestIds.AddHelpIcon(),
                            info: contactPickerTexts.addAdditionalRecipientsTooltip()
                        })
                    ]
                })
            ]
        })
    });
};
